import { useEffect, useRef, useState } from "react";
import apiInstance from "../../apiInstance";
import PopupModal from "../Popups/PopupModal";
import DetailsPage from "./DetailsPage/DetailsPage";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { changeCurrentUser } from "../../../state/actionCreators";

const DevadminUsers = () => {
    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [usersPerPage, setUsersPerPage] = useState(50);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [bulkAction, setBulkAction] = useState("");
    const [reload, setReload] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filters, setFilters] = useState({
        role: "",
    });
    const [error, setError] = useState("");
    const popupRef = useRef();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const deleteUserHandler = (userId) => {
        const updatedUsers = users.filter((item) => userId !== item._id);
        setUsers(updatedUsers);
    };

    const addUserHandler = (user) => {
        setUsers((prev) => [user, ...prev]);
    };

    const updateUserHandler = (user) => {
        const updatedUsers = users.map((item) =>
            item._id === user.id ? { ...item, ...user } : item
        );
        setUsers(updatedUsers);
    };

    const switchUser = (userId) => {
        apiInstance
            .get(`/devadmin/user/switch/${userId}`)
            .then((response) => {
                const user = response.data.user;

                const adminId = localStorage.getItem("userId");
                const adminToken = localStorage.getItem("token");

                sessionStorage.setItem("adminToken", adminToken);
                sessionStorage.setItem("adminId", adminId);
                sessionStorage.setItem("userSwitched", true);

                localStorage.setItem("token", user.token);
                localStorage.setItem("userId", user.id);

                dispatch(changeCurrentUser(user));
                navigate("/");
            })
            .catch((error) => {
                console.error("Error switching user:", error.message);
                setError(error.response.data.message || 'Something went wrong, please try again later.')
            });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allSelectedUserIds = users.map((item) => item._id);
            setSelectedUsers(allSelectedUserIds);
        } else {
            setSelectedUsers([]);
        }
    };

    const handleSelect = (e, userId) => {
        setSelectedUsers((prevSelected) => {
            if (prevSelected.includes(userId)) {
                return prevSelected.filter((id) => id !== userId);
            } else {
                return [...prevSelected, userId];
            }
        });
    };

    const handleBulkAction = async () => {
        switch (bulkAction) {
            case "delete":
                for (const userId of selectedUsers) {
                    try {
                        const res = await apiInstance.delete(
                            `/devadmin/user/${userId}`
                        );

                        setUsers((prev) =>
                            prev.filter((item) => item._id !== userId)
                        );
                    } catch (error) {
                        console.error(`Error deleting user:`, error);
                        setError(`Error deleting user ${userId}.`);
                    }
                }
                setReload((prev) => prev + 1);
                break;
        }
    };

    const handleCurrentPageChange = (p) => {
        const totalPages = Math.ceil(totalUsers / usersPerPage);
        const value = Math.min(totalPages, Math.max(1, p));
        setCurrentPage(value);
        setSelectedUsers([]);
    };

    const renderPaginationControls = () => {
        let totalPages = 0;
        let startItem = 0;
        let endItem = 0;
        if (totalUsers > 0) {
            totalPages = Math.ceil(totalUsers / usersPerPage);
            startItem = (currentPage - 1) * usersPerPage + 1;
            endItem = Math.min(currentPage * usersPerPage, totalUsers);
        }

        return (
            <div className="mjsdev-webs-pagination-wrapper">
                Per Page
                <select
                    value={usersPerPage}
                    onChange={(e) => {
                        setUsersPerPage(Number(e.target.value));
                        handleCurrentPageChange(1);
                    }}
                >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                </select>
                <span>
                    Showing {startItem}-{endItem} of {totalUsers}
                </span>
                <button
                    onClick={() => handleCurrentPageChange(1)}
                    disabled={currentPage === 1}
                >
                    &laquo;
                </button>
                <button
                    onClick={() => handleCurrentPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    &lsaquo;
                </button>
                <input
                    type="number"
                    value={currentPage}
                    onChange={(e) =>
                        handleCurrentPageChange(Number(e.target.value))
                    }
                />
                <span>of {totalPages}</span>
                <button
                    onClick={() => handleCurrentPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    &rsaquo;
                </button>
                <button
                    onClick={() => handleCurrentPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                >
                    &raquo;
                </button>
            </div>
        );
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(search || "");
        }, 400);

        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        const fetchUsers = async () => {
            apiInstance
                .get(`/devadmin/user`, {
                    params: {
                        page: currentPage,
                        limit: usersPerPage,
                        search: debouncedSearch,
                        ...filters,
                    },
                })
                .then((response) => {
                    const fetchedUsers = response.data.users || [];
                    setUsers(fetchedUsers);
                    setTotalUsers(response.data.pagination.total || 0);
                    setSelectedUsers([]);
                })
                .catch((error) => {
                    console.error("Error fetching data:", error);
                    setError("Failed to load users.");
                });
        };
        fetchUsers();
    }, [currentPage, usersPerPage, reload, filters, debouncedSearch]);

    return (
        <>
            <div className="mjsdev-main">
                <div className="mjsdev-webs-header">
                    <h1></h1>
                    <button
                        className="mjs-btn mjsdev-add-user"
                        onClick={(e) => {
                            if (popupRef.current) {
                                popupRef.current.openPopup("addUser");
                            }
                        }}
                    >
                        Add New User
                    </button>
                </div>

                <div className="mjsdev-webs-toolbar">
                    <div className="mjsdev-webs-bulk_actions">
                        <select
                            value={bulkAction}
                            onChange={(e) => setBulkAction(e.target.value)}
                        >
                            <option value="">Select</option>
                            <option value="delete">Delete</option>
                        </select>
                        <button
                            className="mjs-btn"
                            onClick={handleBulkAction}
                            disabled={!bulkAction || selectedUsers.length === 0}
                        >
                            Apply
                        </button>
                    </div>

                    <div className="mjsdev-webs-filters">
                        <div className="mjsdev-search-wrapper">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            {search && (
                                <i
                                    className="fa-solid fa-times"
                                    onClick={() => setSearch("")}
                                    title="Clear search"
                                ></i>
                            )}
                        </div>
                        <select
                            value={filters.role}
                            onChange={(e) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    role: e.target.value,
                                }));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">All Roles</option>
                            <option value="admin">Administrator</option>
                            <option value="Customer">Customer</option>
                        </select>
                    </div>

                    <div className="mjsdev-webs-pgn-top">
                        {renderPaginationControls()}
                    </div>
                </div>

                {error && <p className="mjs-error">{error}</p>}

                <table className="mjsdev-table hover-enabled">
                    <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={
                                        users.length > 0 &&
                                        selectedUsers.length === users.length
                                    }
                                />
                            </th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th></th>
                        </tr>
                    </thead>
                    {users.length > 0 ? (
                        <tbody>
                            {users.map((user, index) => (
                                <tr
                                    key={user._id}
                                    onClick={() => navigate(`/devadmin/users/${user._id}`)}
                                >
                                    <td>
                                        <input
                                            type="checkbox"
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => {
                                                handleSelect(e, user._id);
                                            }}
                                            checked={selectedUsers.includes(
                                                user._id
                                            )}
                                        />
                                    </td>
                                    <td>{user.name || "-"}</td>
                                    <td>{user.email || "-"}</td>
                                    <td>
                                        {(user.role || "customer")
                                            .charAt(0)
                                            .toUpperCase() +
                                            (user.role || "customer").slice(1)}
                                    </td>
                                    <td className="mjsdev-table-actions">
                                        <i
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (popupRef.current) {
                                                    popupRef.current.openPopup(
                                                        "editUser",
                                                        { user }
                                                    );
                                                }
                                            }}
                                            title="Edit User"
                                            className="fa-solid fa-pen-to-square"
                                        ></i>
                                        <i
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                switchUser(user._id)
                                            }}
                                            title="Switch User"
                                            className="fa-solid fa-eye"
                                        ></i>
                                        <i
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (popupRef.current) {
                                                    popupRef.current.openPopup(
                                                        "deleteUser",
                                                        { userId: user._id }
                                                    );
                                                }
                                            }}
                                            title="Delete User"
                                            className="fa-solid fa-trash"
                                        ></i>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    ) : (
                        <p className="mjsdev-msg">No user found.</p>
                    )}
                </table>

                <div className="mjsdev-webs-pgn-bottom">
                    {renderPaginationControls()}
                </div>
            </div>
            <PopupModal
                ref={popupRef}
                deleteUser={deleteUserHandler}
                addUser={addUserHandler}
                updateUser={updateUserHandler}
            />
        </>
    );
};

export default DevadminUsers;
